/**
 * NDAGate — Click-wrap acknowledgment that gates the three proposal pages
 * (/baytown, /north-houston, /compare).
 *
 * IMPORTANT LEGAL NOTE — The acknowledgment text below is INDUSTRY-STANDARD
 * BOILERPLATE drafted to be enforceable in Texas under click-wrap principles
 * (Specht v. Netscape; TX caselaw on online assent). It is NOT a substitute
 * for an attorney-reviewed NDA. George Broughton III / Sterile Mate LLC
 * should have a Texas attorney review this language before relying on it
 * for actual enforcement.
 *
 * Once a reviewer acknowledges, the acceptance is:
 *   1. Logged on the server via POST /api/nda/acknowledge (auditable record)
 *   2. Cached in localStorage so the visitor isn't re-prompted on every page
 *      load (key: bridge_nda_v1)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, ShieldCheck, ArrowRight, Loader2, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NDA_VERSION = "v1.0";
const STORAGE_KEY = "bridge_nda_v1"; // bumped whenever NDA text materially changes

/**
 * Wrap the page body with this component to require the click-wrap
 * acknowledgment before the page renders.
 *
 *   proposal: "baytown" | "north-houston" | "compare"
 *   children: actual page content
 */
export default function NDAGate({ proposal, children }) {
  const [accepted, setAccepted] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return parsed.version === NDA_VERSION && !!parsed.email;
    } catch {
      return false;
    }
  });

  if (accepted) return children;
  return <NDAModal proposal={proposal} onAccepted={() => setAccepted(true)} />;
}

function NDAModal({ proposal, onAccepted }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [role, setRole] = useState("");
  const [cb1, setCb1] = useState(false); // credentialed reviewer
  const [cb2, setCb2] = useState(false); // confidentiality
  const [cb3, setCb3] = useState(false); // no duplication
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const allChecked = cb1 && cb2 && cb3;
  const formValid = fullName.trim() && email.trim() && organization.trim() && allChecked;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      toast.error("Please complete every field and check all three acknowledgments.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/nda/acknowledge`, {
        full_name: fullName,
        email,
        organization,
        role: role || null,
        proposal,
        version: NDA_VERSION,
        agreed_credentialed_reviewer: cb1,
        agreed_confidentiality: cb2,
        agreed_no_duplication: cb3,
      });
      if (data.accepted) {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            version: NDA_VERSION,
            email,
            acknowledgmentId: data.acknowledgment_id,
            acceptedAt: new Date().toISOString(),
          })
        );
        toast.success("Acknowledgment recorded. Opening dossier.");
        onAccepted();
      } else {
        toast.error("Acknowledgment was not recorded. Please try again.");
      }
    } catch (err) {
      toast.error("Could not record acknowledgment. Please try again or contact George Broughton III directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="nda-gate-overlay"
      className="fixed inset-0 z-[100] bg-[#0A192F]/95 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto py-6 sm:py-10"
    >
      <div className="bg-[#F9F7F1] w-full max-w-3xl mx-4 my-auto shadow-2xl border border-[#D4AF37] relative">
        {/* Header */}
        <div className="bg-[#0A192F] text-[#F9F7F1] px-6 lg:px-8 py-5 flex items-start gap-4 border-b border-[#D4AF37]/40">
          <Lock className="h-6 w-6 text-[#D4AF37] shrink-0 mt-1" />
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-1">
              Confidential Document · Reviewer Acknowledgment Required
            </div>
            <div className="font-display text-xl lg:text-2xl leading-tight">
              The Bridge Model · Proposal Dossier
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1] mt-1">
              © Sterile Mate LLC · All Rights Reserved · NDA {NDA_VERSION}
            </div>
          </div>
        </div>

        {/* Legal text */}
        <div className="px-6 lg:px-8 py-5 max-h-[40vh] overflow-y-auto bg-white border-b border-[#CBD5E1]">
          <div className="flex items-start gap-2 mb-4">
            <ScrollText className="h-4 w-4 text-[#8B0000] shrink-0 mt-0.5" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
              Intellectual Property &amp; Confidentiality Notice
            </div>
          </div>
          <div className="text-xs text-[#334155] leading-relaxed space-y-3">
            <p>
              <strong>Authorship &amp; Ownership.</strong> The Bridge Model methodology, including but not limited to its
              Hub-and-Spokes operational architecture, Service Connector caseload structure, three-portfolio service-delivery
              framework, train-the-trainer curriculum, intake-matrix design, financial-forecast templates, contract-taper schedule,
              and the textual content, charts, and graphic design of this dossier (collectively, the &quot;Confidential
              Materials&quot;), are the original, proprietary work product of George Broughton III, sole member of Sterile Mate
              LLC, a Texas limited liability company (&quot;Sterile Mate&quot; or &quot;Disclosing Party&quot;). The Confidential
              Materials are protected under United States copyright law (17 U.S.C. § 101 et seq.), the Texas Uniform Trade Secrets
              Act (Tex. Civ. Prac. &amp; Rem. Code § 134A), and applicable federal law including the Defend Trade Secrets Act
              (18 U.S.C. § 1836).
            </p>
            <p>
              <strong>Limited Permitted Use.</strong> Access to the Confidential Materials is granted solely to credentialed
              reviewers — including municipal officials, district board members, county commissioners and their staff, grant
              reviewers, anchor-employer representatives, philanthropic-foundation officers, and other persons with a bona fide
              evaluative purpose — for the singular and limited purpose of evaluating Sterile Mate&apos;s proposal in connection
              with a possible engagement, contract award, grant decision, or partnership. The recipient (&quot;Receiving
              Party&quot;) acknowledges no other use is authorized.
            </p>
            <p>
              <strong>Confidentiality Covenant.</strong> The Receiving Party shall (a) hold the Confidential Materials in strict
              confidence using the same degree of care it uses to protect its own confidential information of like importance, and
              in no event less than reasonable care; (b) not disclose, share, transmit, or display the Confidential Materials, in
              whole or in part, to any third party who has not independently executed an equivalent confidentiality
              acknowledgment; and (c) limit access within the Receiving Party&apos;s organization to those personnel with a
              demonstrable need-to-know in furtherance of the evaluation purpose.
            </p>
            <p>
              <strong>Anti-Duplication &amp; Anti-Reproduction.</strong> The Receiving Party shall not, directly or indirectly,
              and shall not permit any third party to: (i) copy, reproduce, screenshot, photograph, scan, transcribe, or otherwise
              duplicate the Confidential Materials except for the singular purpose of internal evaluation; (ii) modify, adapt, or
              create derivative works based on the Confidential Materials; (iii) implement, replicate, or commercialize the
              methodology described in the Confidential Materials except pursuant to a fully executed written agreement with
              Sterile Mate; (iv) use the Confidential Materials to inform the selection or operation of any competing service
              provider or consultant; or (v) publish, post, distribute, or display the Confidential Materials on any public
              website, social media platform, or other publicly accessible forum.
            </p>
            <p>
              <strong>No License Granted.</strong> Nothing in this acknowledgment shall be construed as granting the Receiving
              Party any license, ownership interest, or other right in or to the Confidential Materials, whether by implication,
              estoppel, or otherwise, except the limited right to view the Confidential Materials for the evaluative purpose
              stated above.
            </p>
            <p>
              <strong>Term.</strong> The obligations set forth in this acknowledgment shall remain in effect for a period of
              five (5) years from the date of acknowledgment, except that obligations relating to information that constitutes a
              trade secret under applicable law shall continue for as long as such information remains a trade secret.
            </p>
            <p>
              <strong>Governing Law &amp; Venue.</strong> This acknowledgment shall be governed by the laws of the State of Texas
              without regard to its conflict-of-laws principles. Exclusive venue for any dispute arising hereunder shall lie in
              the state or federal courts located in Harris County, Texas, and the Receiving Party consents to personal
              jurisdiction therein.
            </p>
            <p>
              <strong>Remedies.</strong> The Receiving Party acknowledges that monetary damages may be an insufficient remedy for
              breach of this acknowledgment and that Sterile Mate shall be entitled to seek equitable relief, including
              injunctive relief and specific performance, in addition to all other remedies available at law or in equity.
            </p>
            <p>
              <strong>Click-Wrap Assent.</strong> By completing the form below and clicking the acknowledgment button, the
              Receiving Party agrees that (a) it has had a reasonable opportunity to review this acknowledgment in its entirety;
              (b) its assent to these terms constitutes a binding electronic signature under the Texas Uniform Electronic
              Transactions Act (Tex. Bus. &amp; Com. Code Ch. 322) and the federal Electronic Signatures in Global and National
              Commerce Act (15 U.S.C. § 7001 et seq.); and (c) a complete audit record of this acknowledgment — including
              name, email, organization, timestamp, and acknowledgment version — shall be retained by Sterile Mate as
              contemporaneous evidence of consent.
            </p>
            <p className="text-[10px] text-[#8B0000] uppercase tracking-widest font-mono pt-2 border-t border-[#CBD5E1]">
              Draft · Industry-standard click-wrap language · Texas attorney review recommended before relying on this
              acknowledgment for legal enforcement.
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} data-testid="nda-form" className="px-6 lg:px-8 py-5 bg-[#F9F7F1] space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nda-name" className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Full Name *</Label>
              <Input id="nda-name" data-testid="nda-name-input" type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane R. Reviewer" className="rounded-none mt-1.5 h-10" />
            </div>
            <div>
              <Label htmlFor="nda-email" className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Email *</Label>
              <Input id="nda-email" data-testid="nda-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jreviewer@example.gov" className="rounded-none mt-1.5 h-10" />
            </div>
            <div>
              <Label htmlFor="nda-org" className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Organization *</Label>
              <Input id="nda-org" data-testid="nda-org-input" type="text" required value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="City of Baytown · TDHCA · HUD · etc." className="rounded-none mt-1.5 h-10" />
            </div>
            <div>
              <Label htmlFor="nda-role" className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Role / Title (optional)</Label>
              <Input id="nda-role" data-testid="nda-role-input" type="text" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Council aide · Grant officer · Funder" className="rounded-none mt-1.5 h-10" />
            </div>
          </div>

          <div className="space-y-2.5 pt-1">
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="nda-checkbox-1-row">
              <Checkbox id="nda-cb1" checked={cb1} onCheckedChange={(v) => setCb1(!!v)} className="mt-0.5" data-testid="nda-checkbox-1" />
              <span className="text-xs text-[#334155] leading-relaxed">
                I am a <strong>credentialed reviewer</strong> accessing this dossier for the singular purpose of evaluating Sterile Mate LLC&apos;s proposal.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="nda-checkbox-2-row">
              <Checkbox id="nda-cb2" checked={cb2} onCheckedChange={(v) => setCb2(!!v)} className="mt-0.5" data-testid="nda-checkbox-2" />
              <span className="text-xs text-[#334155] leading-relaxed">
                I acknowledge the dossier is the <strong>proprietary work of Sterile Mate LLC</strong>, protected by copyright and trade-secret law, and I will <strong>hold its contents in strict confidence</strong>.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="nda-checkbox-3-row">
              <Checkbox id="nda-cb3" checked={cb3} onCheckedChange={(v) => setCb3(!!v)} className="mt-0.5" data-testid="nda-checkbox-3" />
              <span className="text-xs text-[#334155] leading-relaxed">
                I agree <strong>not to copy, reproduce, distribute, replicate, or implement</strong> the methodology described in this dossier except pursuant to a written agreement with Sterile Mate LLC.
              </span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={submitting || !formValid}
            data-testid="nda-submit-btn"
            className="rounded-none w-full bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>I Acknowledge · Open Dossier <ArrowRight className="ml-2 h-4 w-4" /></>)}
          </Button>
          <div className="flex items-center gap-2 text-[10px] text-[#64748B] justify-center font-mono uppercase tracking-widest">
            <ShieldCheck className="h-3 w-3" /> This acknowledgment is logged for audit purposes
          </div>
        </form>
      </div>
    </div>
  );
}
